import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Loader2 } from 'lucide-react';
import type { PageProps } from '@/types/page';

interface AccessDeniedPageProps extends PageProps {
    title?: string;
    message?: string;
    onBack?: () => void;
    onHome?: () => void;
}

export function AccessDeniedPage({
    nav = true,
    footer = true,
    title = "Access Restricted",
    message = "You don't have permission to access this resource.",
    onBack,
    onHome
}: AccessDeniedPageProps) {
    const navigate = useNavigate();

    // back and delete history
    const handleBack = onBack || (() => {
        navigate('/app/sessions');
        window.history.replaceState(null, '', '/app/sessions');
    });
    const handleHome = onHome || (() => {
        navigate('/app');
        window.history.replaceState(null, '', '/app');
    });

    return (
        <PageLayout nav={nav} footer={footer}>
            <div className="container mx-auto max-w-2xl py-8">
                <Card className="p-8 text-center">
                    <div className="flex flex-col items-center gap-4">
                        <div className="p-3 bg-orange-100 dark:bg-orange-950/50 rounded-full">
                            <Users className="h-8 w-8 text-orange-600 dark:text-orange-400" />
                        </div>

                        <div className="space-y-2">
                            <h2 className="text-2xl font-semibold">{title}</h2>
                            <p className="text-muted-foreground">{message}</p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 mt-6">
                            <Button
                                variant="outline"
                                onClick={handleBack}
                                className="flex items-center gap-2"
                            >
                                Back to Sessions
                            </Button>
                            <Button
                                onClick={handleHome}
                                className="flex items-center gap-2"
                            >
                                Return to Dashboard
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        </PageLayout>
    );
}

interface LoadingPageProps extends PageProps {
    message?: string;
}

export function LoadingPage({ nav = true, footer = true, message = "Loading..." }: LoadingPageProps) {
    return (
        <PageLayout nav={nav} footer={footer}>
            <div className="flex items-center justify-center min-h-screen">
                <div className="flex items-center gap-3">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span>{message}</span>
                </div>
            </div>
        </PageLayout>
    );
}
